import { FC, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { push } from 'connected-react-router';
import { isAccountStatusReadOnly } from 'utils/clickme';
import { CREATE_MENU_PAGE_ROUTE } from 'utils/constants/app';

import headerButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/header/header_button_click';
import { Button, useBreakpoint } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { fetchWizardVacanciesAndFilters } from 'components/CampaignCreationWizard/fetchWizardVacanciesAndFilters';
import { Message } from 'components/common/Message';
import { HeaderBalance } from 'components/Header/HeaderBalance/HeaderBalance';
import { HeaderNav } from 'components/Header/HeaderNav/HeaderNav';
import { HeaderUser } from 'components/Header/HeaderUser/HeaderUser';
import { Login } from 'components/Header/Login';
import { HelpModal } from 'components/HelpModal/HelpModal';
import { Logo, LogoSize } from 'components/Logo/Logo';

import { resetCampaignCreationWizard } from 'store/models/campaignCreationWizard';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/Header/Header.module.less';

const enum DropKey {
    Balance = 'balance',
    UserInfo = 'userInfo',
}

const TrlKeys = {
    createCampaign: 'Header.HeaderNav.createCampaign',
};

export const Header: FC = () => {
    const user = useSelector(({ user }) => user.user);
    const isReadOnly = isAccountStatusReadOnly(user?.currentAccount.status);

    const [activeDropKey, setActiveDropKey] = useState<DropKey | null>(null);

    const dropActivatorRef = useRef(null);

    const trls = useTrls();
    const dispatch = useDispatch();
    const { isMobile } = useBreakpoint();
    const location = useLocation();

    const handleCreateCampaignClick = () => {
        headerButtonClick({ buttonName: 'campaign_create_header' });
        if (location.pathname === CREATE_MENU_PAGE_ROUTE) {
            dispatch(resetCampaignCreationWizard(null));
            void dispatch(fetchWizardVacanciesAndFilters());
        }
        dispatch(push(CREATE_MENU_PAGE_ROUTE));
    };

    return (
        <>
            <Message />
            <header className={styles.headerWrapper}>
                <div className={styles.header}>
                    {user ? (
                        <>
                            <div className={styles.headerGroup}>
                                <Logo size={isMobile ? LogoSize.Small : undefined} />
                                {!isMobile && <HeaderNav />}
                            </div>
                            <div className={styles.headerGroup} ref={dropActivatorRef}>
                                {!isMobile && (
                                    <>
                                        <HeaderBalance
                                            dropActivatorRef={dropActivatorRef}
                                            isDropVisible={activeDropKey === DropKey.Balance}
                                            setIsDropVisibleOn={() => setActiveDropKey(DropKey.Balance)}
                                            setIsDropVisibleOff={() => setActiveDropKey(null)}
                                        />
                                        {!isReadOnly && (
                                            <Button
                                                mode="secondary"
                                                style="constant"
                                                size="small"
                                                onClick={handleCreateCampaignClick}
                                            >
                                                {trls(TrlKeys.createCampaign)}
                                            </Button>
                                        )}
                                    </>
                                )}
                                <HeaderUser
                                    dropActivatorRef={dropActivatorRef}
                                    isDropVisible={activeDropKey === DropKey.UserInfo}
                                    setIsDropVisibleOn={() => setActiveDropKey(DropKey.UserInfo)}
                                    setIsDropVisibleOff={() => setActiveDropKey(null)}
                                />
                                {isMobile && <HeaderNav />}
                            </div>
                        </>
                    ) : (
                        <>
                            <Logo size={isMobile ? LogoSize.Small : undefined} />
                            <Login />
                        </>
                    )}
                </div>
            </header>
            <HelpModal />
        </>
    );
};
